import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { getPackages, createPackage, updatePackage, deletePackage } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    duration_days: 30,
    features: [],
    is_active: true
  });
  const [featuresText, setFeaturesText] = useState('');

  const fetchPackages = async () => {
    try {
      const response = await getPackages();
      setPackages(response.data);
    } catch (error) {
      toast.error('Paketler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const filteredPackages = packages.filter(pkg =>
    pkg.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description || '',
        price: pkg.price,
        duration_days: pkg.duration_days,
        features: pkg.features || [],
        is_active: pkg.is_active
      });
      setFeaturesText((pkg.features || []).join('\n'));
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        duration_days: 30,
        features: [],
        is_active: true
      });
      setFeaturesText('');
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const features = featuresText.split('\n').filter(f => f.trim());
    const data = { ...formData, features };
    
    try {
      if (editingPackage) {
        await updatePackage(editingPackage.id, data);
        toast.success('Paket güncellendi');
      } else {
        await createPackage(data);
        toast.success('Paket eklendi');
      }
      setDialogOpen(false);
      fetchPackages();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`${pkg.name} paketini silmek istediğinize emin misiniz?`)) return;
    try {
      await deletePackage(pkg.id);
      toast.success('Paket silindi');
      fetchPackages();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  return (
    <div className="space-y-6 page-enter" data-testid="admin-packages">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Paket ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-card border-white/10"
            data-testid="package-search"
          />
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-package-btn">
          <Plus className="w-4 h-4 mr-2" /> Paket Ekle
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead>Paket Adı</TableHead>
              <TableHead>Fiyat</TableHead>
              <TableHead>Süre</TableHead>
              <TableHead>Özellikler</TableHead>
              <TableHead className="w-24">Durum</TableHead>
              <TableHead className="w-32 text-right">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredPackages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  {searchQuery ? 'Paket bulunamadı' : 'Henüz paket eklenmemiş'}
                </TableCell>
              </TableRow>
            ) : (
              filteredPackages.map(pkg => (
                <TableRow key={pkg.id} className="border-white/10" data-testid={`package-row-${pkg.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{pkg.name}</p>
                      {pkg.description && (
                        <p className="text-muted-foreground text-sm">{pkg.description}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-heading text-primary">
                    {pkg.price.toLocaleString('tr-TR')} ₺
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {pkg.duration_days} gün
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(pkg.features || []).slice(0, 2).map((f, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {f}
                        </Badge>
                      ))}
                      {(pkg.features || []).length > 2 && (
                        <Badge variant="secondary" className="text-xs">
                          +{pkg.features.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={pkg.is_active ? 'default' : 'secondary'}>
                      {pkg.is_active ? 'Aktif' : 'Pasif'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenDialog(pkg)}
                      data-testid={`edit-package-${pkg.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(pkg)}
                      data-testid={`delete-package-${pkg.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>
              {editingPackage ? 'Paket Düzenle' : 'Yeni Paket'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Paket Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Standart Paket"
                required
                className="bg-secondary border-white/10"
                data-testid="package-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Paket açıklaması"
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">Fiyat (₺) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  required
                  className="bg-secondary border-white/10"
                  data-testid="package-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration_days">Süre (gün) *</Label>
                <Input
                  id="duration_days"
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 30 })}
                  required
                  className="bg-secondary border-white/10"
                  data-testid="package-duration-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="features">Özellikler (her satıra bir özellik)</Label>
              <Textarea
                id="features"
                value={featuresText}
                onChange={(e) => setFeaturesText(e.target.value)}
                placeholder="Sınırsız görüntülenme&#10;Öncelikli listeleme&#10;WhatsApp butonu"
                rows={4}
                className="bg-secondary border-white/10"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Aktif</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" data-testid="save-package-btn">
                {editingPackage ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
